import React, { useState } from 'react';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Info,
  Download,
  FileText,
  Loader
} from 'lucide-react';
import { useProject } from '../contexts/ProjectContext';
import api from '../api';
import toast from 'react-hot-toast';

interface QCRule {
  ruleId: number;
  ruleName: string;
  status: 'Pass' | 'Fail' | 'N/A' | 'Info';
  details: string | null;
  affectedColumns?: string[] | null;
}

interface QCResults {
  summary: {
    totalRules: number;
    passed: number;
    failed: number;
    na: number;
    info: number;
  };
  rules: QCRule[];
  metadata: {
    validatedAt: string;
    filename: string;
    projectId: string;
    userId: string;
  };
}

const RiskTemplateQC: React.FC = () => {
  const { currentProject } = useProject();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [qcResults, setQcResults] = useState<QCResults | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validExtensions = ['.xlsx', '.xls'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        toast.error('Please upload an Excel file (.xlsx or .xls)');
        return;
      }

      setSelectedFile(file);
      setQcResults(null); // Clear previous results
    }
  };

  const handleUploadAndValidate = async () => {
    if (!selectedFile || !currentProject) {
      toast.error('Please select a file and ensure a project is selected');
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('template', selectedFile);
      formData.append('projectId', currentProject.id.toString());

      toast.loading('Uploading and validating template...', { id: 'qc-upload' });

      const response = await api.post('/api/risk-template-qc/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setQcResults(response.data.results);

      toast.success(
        `QC Validation Complete! ${response.data.results.summary.passed}/${response.data.results.summary.totalRules} rules passed`,
        { id: 'qc-upload', duration: 5000 }
      );

    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(
        error.response?.data?.error || 'Failed to validate template',
        { id: 'qc-upload' }
      );
    } finally {
      setUploading(false);
    }
  };

  const handleExport = async (format: 'excel' | 'pdf') => {
    if (!qcResults) return;

    try {
      toast.loading(`Exporting to ${format.toUpperCase()}...`, { id: 'qc-export' });

      const response = await api.post('/api/risk-template-qc/export', {
        results: qcResults,
        format
      }, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `QC_Results_${Date.now()}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Exported to ${format.toUpperCase()}`, { id: 'qc-export' });

    } catch (error: any) {
      console.error('Export error:', error);
      toast.error('Failed to export results', { id: 'qc-export' });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Pass':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'Fail':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'N/A':
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
      case 'Info':
        return <Info className="w-5 h-5 text-blue-600" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = 'px-3 py-1 rounded-full text-sm font-medium';

    switch (status) {
      case 'Pass':
        return <span className={`${baseClasses} bg-green-100 text-green-800`}>✓ Pass</span>;
      case 'Fail':
        return <span className={`${baseClasses} bg-red-100 text-red-800`}>✗ Fail</span>;
      case 'N/A':
        return <span className={`${baseClasses} bg-gray-100 text-gray-600`}>N/A</span>;
      case 'Info':
        return <span className={`${baseClasses} bg-blue-100 text-blue-800`}>ⓘ Info</span>;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Risk Template Quality Check
          </h1>
          <p className="text-gray-600">
            Upload your completed RCSA Risk Template to validate against 20 ECM Quality Rules
          </p>
        </div>

        {/* Upload Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-blue-600" />
            Upload Risk Template
          </h2>

          <div className="space-y-4">
            {/* File Input */}
            <div className="flex items-center gap-4">
              <label
                htmlFor="file-upload"
                className="flex-1 cursor-pointer border-2 border-dashed border-gray-300 rounded-lg p-8 hover:border-blue-500 transition-colors"
              >
                <div className="flex flex-col items-center justify-center text-center">
                  <Upload className="w-12 h-12 text-gray-400 mb-3" />
                  {selectedFile ? (
                    <>
                      <p className="text-sm font-medium text-gray-900 mb-1">
                        {selectedFile.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-gray-900 mb-1">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">
                        Excel files only (.xlsx, .xls) - Max 10MB
                      </p>
                    </>
                  )}
                </div>
                <input
                  id="file-upload"
                  type="file"
                  className="hidden"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                />
              </label>
            </div>

            {/* Upload Button */}
            <button
              onClick={handleUploadAndValidate}
              disabled={!selectedFile || uploading || !currentProject}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>Validating Template...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5" />
                  <span>Upload & Run Quality Check</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Results Section */}
        {qcResults && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <p className="text-sm text-gray-600 mb-1">Total Rules</p>
                <p className="text-2xl font-bold text-gray-900">{qcResults.summary.totalRules}</p>
              </div>

              <div className="bg-green-50 rounded-lg shadow-sm border border-green-200 p-4">
                <p className="text-sm text-green-700 mb-1">Passed</p>
                <p className="text-2xl font-bold text-green-900">{qcResults.summary.passed}</p>
              </div>

              <div className="bg-red-50 rounded-lg shadow-sm border border-red-200 p-4">
                <p className="text-sm text-red-700 mb-1">Failed</p>
                <p className="text-2xl font-bold text-red-900">{qcResults.summary.failed}</p>
              </div>

              <div className="bg-gray-50 rounded-lg shadow-sm border border-gray-200 p-4">
                <p className="text-sm text-gray-600 mb-1">N/A</p>
                <p className="text-2xl font-bold text-gray-900">{qcResults.summary.na}</p>
              </div>

              <div className="bg-blue-50 rounded-lg shadow-sm border border-blue-200 p-4">
                <p className="text-sm text-blue-700 mb-1">Info</p>
                <p className="text-2xl font-bold text-blue-900">{qcResults.summary.info}</p>
              </div>
            </div>

            {/* Export Buttons */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Export Results</h3>
                  <p className="text-sm text-gray-600">Download QC results for reporting</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleExport('excel')}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    <span>Export Excel</span>
                  </button>
                  <button
                    onClick={() => handleExport('pdf')}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    <span>Export PDF</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Results Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-blue-600" />
                  QC Validation Results
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Template: {qcResults.metadata.filename} | Validated: {new Date(qcResults.metadata.validatedAt).toLocaleString()}
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                        Rule ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ECM Quality Rule
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                        QC Result
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {qcResults.rules.map((rule) => (
                      <tr
                        key={rule.ruleId}
                        className={`hover:bg-gray-50 ${
                          rule.status === 'Fail' ? 'bg-red-50' :
                          rule.status === 'Pass' ? 'bg-green-50' : ''
                        }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {rule.ruleId}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div>
                            <p className="font-medium mb-1">{rule.ruleName}</p>
                            {rule.details && (
                              <p className="text-xs text-gray-600 mt-1 bg-gray-100 p-2 rounded">
                                {rule.details}
                              </p>
                            )}
                            {rule.affectedColumns && rule.affectedColumns.length > 0 && (
                              <p className="text-xs text-orange-600 mt-1">
                                Affected columns: {rule.affectedColumns.join(', ')}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(rule.status)}
                            {getStatusBadge(rule.status)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* No Results State */}
        {!qcResults && !uploading && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <FileSpreadsheet className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Results Yet
            </h3>
            <p className="text-gray-600">
              Upload a Risk Template to run quality checks
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RiskTemplateQC;
